import { useState, useMemo } from 'react';
import { Copy } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

interface Conversion {
  id: string;
  name: string;
  fn: (s: string) => string;
}

const TRANSFORMS: Conversion[] = [
  { id: 'upper', name: 'UPPERCASE', fn: (s) => s.toUpperCase() },
  { id: 'lower', name: 'lowercase', fn: (s) => s.toLowerCase() },
  {
    id: 'title',
    name: 'Title Case',
    fn: (s) =>
      s
        .toLowerCase()
        .replace(/\b([a-z])/g, (m) => m.toUpperCase()),
  },
  {
    id: 'sentence',
    name: 'Sentence case',
    fn: (s) => s.toLowerCase().replace(/(^|\.\s+)([a-z])/g, (_, p, c) => p + c.toUpperCase()),
  },
  {
    id: 'camel',
    name: 'camelCase',
    fn: (s) => words(s).map((w, i) => (i === 0 ? w.toLowerCase() : cap(w))).join(''),
  },
  {
    id: 'pascal',
    name: 'PascalCase',
    fn: (s) => words(s).map(cap).join(''),
  },
  {
    id: 'snake',
    name: 'snake_case',
    fn: (s) => words(s).map((w) => w.toLowerCase()).join('_'),
  },
  {
    id: 'kebab',
    name: 'kebab-case',
    fn: (s) => words(s).map((w) => w.toLowerCase()).join('-'),
  },
  {
    id: 'constant',
    name: 'CONSTANT_CASE',
    fn: (s) => words(s).map((w) => w.toUpperCase()).join('_'),
  },
  {
    id: 'dot',
    name: 'dot.case',
    fn: (s) => words(s).map((w) => w.toLowerCase()).join('.'),
  },
  {
    id: 'reverse',
    name: 'esrever',
    fn: (s) => s.split('').reverse().join(''),
  },
  {
    id: 'alternate',
    name: 'aLtErNaTiNg',
    fn: (s) =>
      s
        .split('')
        .map((c, i) => (i % 2 ? c.toUpperCase() : c.toLowerCase()))
        .join(''),
  },
];

function words(s: string): string[] {
  return s.split(/[\s_\-./]+|(?=[A-Z])/).filter(Boolean);
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export function CaseConverter() {
  const [input, setInput] = useState('Hello world from Utility OS');

  const results = useMemo(() => TRANSFORMS.map((t) => ({ ...t, output: t.fn(input) })), [input]);

  return (
    <div>
      <ToolHeader
        toolId="case-converter"
        title="Case converter"
        description="Convert text between 12 cases — UPPER, lower, camel, snake, kebab, constant, dot, alternating, and more."
      />

      <Card className="mb-4 !p-0">
        <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
          <div className="text-xs font-medium text-fg-muted">Source</div>
          <div className="text-2xs text-fg-subtle font-mono">
            {input.length} chars · {words(input).length} words
          </div>
        </div>
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="!border-0 !rounded-t-none min-h-[120px] !bg-transparent focus-visible:!ring-0"
          placeholder="Type or paste text…"
        />
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {results.map((r) => (
          <Card key={r.id} className="group !p-3">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-2xs uppercase tracking-wider text-fg-subtle font-mono">
                {r.name}
              </div>
              <button
                onClick={async () => {
                  const ok = await copyToClipboard(r.output);
                  if (ok) toast.success(`Copied ${r.name}`);
                }}
                className="opacity-0 group-hover:opacity-100 transition text-fg-subtle hover:text-fg"
              >
                <Copy size={11} />
              </button>
            </div>
            <div className="font-mono text-sm break-all min-h-[1.5rem]">{r.output || ' '}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default CaseConverter;
